import {
  DownOutlined,
  LeftOutlined,
  SaveOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, Form, Input, Select, TimePicker } from "antd";
import AddFinanceContact from "components/apps/Contact/AddContact";
import EditContactModal from "components/apps/Contact/EditContact";
import ContactSelector from "components/apps/conversations/createChat/ContactSelector";
import MeetingTagSelect from "components/apps/MeetingSchedule/Tags/MeetingTagSelect";
import MeetingWhatsappPreview from "./MeetingWhatsappPreview";
import AutoAgentNotice from "./AutoSchedule/AutoAgentNotice";
import dayjs from "dayjs";
import { useGetContactDetail } from "queries/contact";
import { useMSScheduleStatuses } from "queries/meetingSchedule";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { websiteIdsSelector, salutationsSelector } from "appredux/selectors";
import { ONLY_FOR_CRMKLEDO } from "utils/static";
import {
  getInhouseCompanyNames,
  isContactValidForInternal,
} from "utils/contactValidation";
import { useTranslation } from "react-i18next";
import {
  formatStatusLabel,
} from "components/apps/MeetingSchedule/AllSchedule/Table/scheduleRowFields";

const { TextArea } = Input;

const FourthStepSchedule = ({
  prev,
  createLoading,
  disabledPrev,
  dataForm,
  onChangeDataForm,
  inhouseUsers,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";
  const websiteId = useSelector(websiteIdsSelector);
  const isInternal = ONLY_FOR_CRMKLEDO.includes(websiteId);
  const isAuto = dataForm.modeForm === "auto";
  const { data: allStatuses = [] } = useMSScheduleStatuses(locale);
  const EXCLUDED_STATUS_NAMES = ["selesai", "tidak hadir"];
  const activeStatuses = useMemo(
    () =>
      allStatuses.filter((s) => {
        if (!s.is_active_for_overlap) return false;
        const label = formatStatusLabel(s, locale).toLowerCase();
        return !EXCLUDED_STATUS_NAMES.includes(label);
      }),
    [allStatuses, locale]
  );
  const [form] = Form.useForm();
  const watchedStatusId = Form.useWatch("status_id", form);
  const watchedTime = Form.useWatch("time", form);
  const previewStartTime =
    watchedTime?.[0]?.format?.("HH:mm") || dataForm.start_time;
  const previewEndTime =
    watchedTime?.[1]?.format?.("HH:mm") || dataForm.end_time;
  const selectedStatusMeta = useMemo(
    () => activeStatuses.find((s) => Number(s.id) === Number(watchedStatusId)) ?? null,
    [activeStatuses, watchedStatusId]
  );
  const defaultAppliedRef = useRef(false);
  useEffect(() => {
    if (activeStatuses.length > 0 && !defaultAppliedRef.current) {
      const current = form.getFieldValue("status_id");
      if (!current) {
        form.setFieldsValue({ status_id: activeStatuses[0].id });
      }
      defaultAppliedRef.current = true;
    }
  }, [activeStatuses, form]);
  const [isOpenAddContact, setIsOpenAddContact] = useState(false);
  const [editContactId, setEditContactId] = useState(null);
  const [selectedContactData, setSelectedContactData] = useState(null);
  const [detailContactData, setDetailContactData] = useState(null);
  const [isUsingSelectedTime, setIsUsingSelectedTime] = useState(true);

  const onHideAddGroup = () => {
    setIsOpenAddContact(false);
  };

  const addGroupHandler = () => {
    setIsOpenAddContact(true);
  };
  const callbackHandler = (response) => {
    if (response.data.data.id) {
      setSelectedContactData(response.data.data);
      setDetailContactData(response.data.data);
      form.setFieldsValue({
        contact_id: response.data.data.id,
      });
    }
  };

  // Kontak yang dipilihkan otomatis (dari percakapan / Ubah Jadwal).
  const prefillId = dataForm.contact?.id || null;
  const prefillShownRef = useRef(false);
  const prefillValidatedRef = useRef(false);

  // Detail lengkap utk validasi internal (punya salutation_id, nama & perusahaan
  // tersimpan) — object dataForm.contact dari percakapan tak membawa salutation_id.
  const { data: prefillDetail } = useGetContactDetail(prefillId, {
    enabled: isInternal && !!prefillId,
  });

  // 1) Tampilkan kontak otomatis sekali (perilaku lama dipertahankan).
  useEffect(() => {
    const c = dataForm.contact;
    if (!c || prefillShownRef.current) return;
    prefillShownRef.current = true;
    setSelectedContactData(c);
    setDetailContactData(c);
    form.setFieldsValue({ contact_id: c.id });
  }, [dataForm.contact, form]);

  // 2) Validasi internal pakai detail lengkap, sekali → buka modal edit bila
  //    nama/perusahaan/sapaan tidak valid (sama seperti saat dipilih user).
  useEffect(() => {
    if (!isInternal || !prefillDetail || prefillValidatedRef.current) return;
    prefillValidatedRef.current = true;
    // Perusahaan bawaan inhouse yang huruf besar semua tidak dianggap invalid.
    const inhouseCompanies = getInhouseCompanyNames(inhouseUsers);
    if (!isContactValidForInternal(prefillDetail, { inhouseCompanies })) {
      setEditContactId(prefillDetail.id);
    }
  }, [isInternal, prefillDetail, inhouseUsers]);

  const onSubmitHandler = () => {
    form
      .validateFields()
      .then((values) => {
        onChangeDataForm(
          {
            contact_id: values.contact_id,
            note: values.note,
            contact: detailContactData,
            start_time: values.time[0].format("HH:mm"),
            end_time: values.time[1].format("HH:mm"),
            status_id: values.status_id ?? null,
            tag_ids: values.tag_ids ?? [],
          },
          true
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };
  const onChangeContactHandler = (_, option) => {
    if (!option) {
      setDetailContactData(null);
      return;
    }
    // Internal: kontak existing harus lolos validasi (nama, perusahaan, sapaan).
    // Jika tidak, buka modal edit & tunda finalisasi pemilihan.
    if (isInternal && !isContactValidForInternal(option)) {
      setEditContactId(option.id);
      return;
    }
    setDetailContactData(option);
  };

  // Kontak selesai diperbaiki & valid → lanjutkan alur pemilihan seperti biasa.
  const onSavedEditContact = (updated) => {
    if (updated?.id) {
      setSelectedContactData(updated);
      setDetailContactData(updated);
      form.setFieldsValue({ contact_id: updated.id });
    }
    setEditContactId(null);
  };

  // Batal memperbaiki → batalkan pemilihan kontak.
  const onCancelEditContact = () => {
    form.setFieldsValue({ contact_id: null });
    setSelectedContactData(null);
    setDetailContactData(null);
    setEditContactId(null);
  };

  const getDetailSelectedContactData = useMemo(() => {
    if (!detailContactData) return "";
    return {
      name: detailContactData.name_only || detailContactData.name,
      email: detailContactData.email,
      phone: detailContactData.phone,
      company: detailContactData.company,
    };
  }, [detailContactData]);

  // Sapaan kontak (untuk pratinjau WA) dari salutation_id → daftar salutations.
  const salutations = useSelector(salutationsSelector);
  const previewSalutation = useMemo(() => {
    const sid = detailContactData?.salutation_id;
    if (!sid) return "";
    return (
      (salutations || []).find((s) => Number(s.id) === Number(sid))
        ?.salutation || ""
    );
  }, [detailContactData, salutations]);

  const initialValues = useMemo(() => {
    return {
      time: [
        dayjs(dataForm.start_time, "HH:mm"),
        dayjs(dataForm.end_time, "HH:mm"),
      ],
      contact_id: dataForm.contact?.id || null,
      note: dataForm.note || "",
      status_id: dataForm.status_id || undefined,
      tag_ids: dataForm.tag_ids || [],
    };
  }, [dataForm]);

  const handleOnChangeCheckbox = (e) => {
    if (e.target.checked) {
      const startTime = dayjs(dataForm.start_time, "HH:mm");
      const endTime = dayjs(dataForm.end_time, "HH:mm");
      form.setFieldsValue({
        time: [startTime, endTime],
      });
    }
    setIsUsingSelectedTime(e.target.checked);
  };

  return (
    <>
      <div>
        <div className="text-center text-xl">
          {t("meeting_schedule.title_fourth_step")}
          {dataForm.date && dayjs(dataForm.date).isValid() && (
            <> {dayjs(dataForm.date).format("DD MMMM YYYY")}</>
          )}
          {dataForm.start_time && dataForm.end_time && (
            <> {t("meeting_schedule.at")} {dataForm.start_time} - {dataForm.end_time}</>
          )}
        </div>
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:justify-center">
          <Form
            layout="vertical"
            form={form}
            className="w-full max-w-xl flex flex-col gap-y-3 lg:w-1/2"
            initialValues={initialValues}
          >
            <div className="flex gap-x-3">
              <Form.Item className="mb-0 flex-1">
                <Form.Item
                  label={t("date.time")}
                  name="time"
                  required
                  className="mb-0"
                  rules={[
                    {
                      required: true,
                      message: t("meeting_schedule.time_required"),
                    },
                  ]}
                  labelCol={{
                    style: { paddingBottom: "0px", fontWeight: 600 },
                  }}
                >
                  <TimePicker.RangePicker
                    className="w-full"
                    format="HH:mm"
                    disabled={isUsingSelectedTime}
                    allowClear={false}
                  />
                </Form.Item>
                {!isAuto && (
                  <Checkbox
                    className="mt-1"
                    onChange={handleOnChangeCheckbox}
                    checked={isUsingSelectedTime}
                  >
                    Gunakan waktu yang terpilih
                  </Checkbox>
                )}
              </Form.Item>
              {activeStatuses.length > 0 && (
                <div
                  className="schedule-status-select-cell schedule-status-select-cell--field flex-1"
                  data-has-color={selectedStatusMeta?.color ? "true" : "false"}
                  style={{
                    "--schedule-status-sel-fg": "#000",
                    ...(selectedStatusMeta?.color
                      ? {
                          "--schedule-status-sel-bg": selectedStatusMeta.color,
                        }
                      : {}),
                  }}
                >
                  <Form.Item
                    label={t("meeting_management.status")}
                    name="status_id"
                    className="mb-0"
                    labelCol={{
                      style: { paddingBottom: "0px", fontWeight: 600 },
                    }}
                  >
                    <Select
                      className="w-full !text-black"
                      variant={
                        selectedStatusMeta?.color ? "borderless" : "outlined"
                      }
                      allowClear
                      placeholder="—"
                      suffixIcon={<DownOutlined className="!text-black" />}
                      popupClassName="ms-schedule-status-select-dropdown"
                      styles={{ popup: { root: { padding: 0 } } }}
                    >
                      {activeStatuses.map((s) => (
                        <Select.Option
                          key={s.id}
                          value={s.id}
                          style={{
                            backgroundColor: s.color || "#f0f0f0",
                            color: "#000",
                          }}
                          className="ms-schedule-status-option rounded-none font-medium !text-black"
                        >
                          {formatStatusLabel(s, locale)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              )}
            </div>
            <ContactSelector
              rules={[
                {
                  required: true,
                  message: t("please_select_contact"),
                },
              ]}
              label={t("contact")}
              selectedContact={selectedContactData}
              className="mb-0"
              labelCol={{ style: { paddingBottom: "0px", fontWeight: 600 } }}
              autocompleteProps={{
                withExtra: true,
                titleExtra: t("add_contact"),
                onClickExtra: addGroupHandler,
                onChange: onChangeContactHandler,
              }}
            />
            {getDetailSelectedContactData && (
              <div className="flex items-center gap-x-1.5">
                <WhatsAppOutlined className="text-green-600" />
                <span>{getDetailSelectedContactData.phone}</span>
              </div>
            )}
            <Form.Item
              label={t("meeting_management.tags")}
              name="tag_ids"
              className="mb-0"
              labelCol={{ style: { paddingBottom: "0px", fontWeight: 600 } }}
            >
              <MeetingTagSelect
                placeholder={t("meeting_management.add_tags")}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              label={t("meeting_management.description")}
              name="note"
              labelCol={{ style: { paddingBottom: "0px", fontWeight: 600 } }}
              rules={[
                {
                  required: true,
                  message: t("meeting_schedule.description_required"),
                },
              ]}
            >
              <TextArea
                showCount
                maxLength={1000}
                placeholder={t("meeting_schedule.description_placeholder")}
                style={{
                  height: 120,
                  resize: "none",
                }}
              />
            </Form.Item>
            {isAuto && (
              <AutoAgentNotice
                agentName={dataForm.auto_agent_name}
                reasoning={dataForm.auto_reasoning}
                scheduleDate={dataForm.date}
              />
            )}
          </Form>
          {isInternal && (
            <div className="hidden shrink-0 lg:block lg:w-[340px]">
              <MeetingWhatsappPreview
                name={getDetailSelectedContactData?.name}
                salutation={previewSalutation}
                company={getDetailSelectedContactData?.company}
                dateLabel={
                  dataForm.date
                    ? dayjs(dataForm.date).format("DD MMMM YYYY")
                    : ""
                }
                startTime={previewStartTime}
                endTime={previewEndTime}
                phoneNumberId={dataForm.dataGroup?.phone_number_id}
              />
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-between">
          <div>
            {!disabledPrev && (
              <Button icon={<LeftOutlined />} onClick={() => prev()}>
                {t("meeting_schedule.previous")}
              </Button>
            )}
          </div>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSubmitHandler}
            loading={createLoading}
          >
            {t("button.save")}
          </Button>
        </div>
        <div className="">
          <AddFinanceContact
            show={isOpenAddContact}
            hideHandler={onHideAddGroup}
            callback={callbackHandler}
            meetingMode
          />
          <EditContactModal
            show={editContactId != null}
            id={editContactId}
            hideHandler={onCancelEditContact}
            onSaved={onSavedEditContact}
            meetingMode
            // Data inhouse hanya milik kontak dari conversation, bukan kontak lain
            // yang dipilih manual di selector.
            inhouseUsers={
              editContactId != null && editContactId === prefillId
                ? inhouseUsers
                : undefined
            }
          />
        </div>
      </div>
    </>
  );
};

export default FourthStepSchedule;
